import { useEffect, useState } from "react";
import { Sparkles, Heart, UserX, X } from "lucide-react";

/**
 * Reveal Ceremony — animated "identity reveal" moment shown when a match happens.
 * If the matched user was anonymous, we play a 3-phase reveal:
 *   Phase 0 (0.0s) — silhouette + "It's a Match" title
 *   Phase 1 (1.2s) — sparkles orbit, silhouette blurs out
 *   Phase 2 (2.4s) — real photo fades in with a starburst
 * For non-anonymous matches, we skip straight to phase 2.
 */
export default function RevealCeremony({ matchPopup, onClose }) {
  const wasAnonymous = matchPopup.revealed?.was_anonymous || matchPopup.profile.is_anonymous;
  const [phase, setPhase] = useState(wasAnonymous ? 0 : 2);

  const revealedName = matchPopup.revealed?.name || matchPopup.profile.name;
  const revealedPhoto = matchPopup.revealed?.photos?.[0] || matchPopup.profile.photos?.[0];

  useEffect(() => {
    if (!wasAnonymous) return;
    const t1 = setTimeout(() => setPhase(1), 1200);
    const t2 = setTimeout(() => setPhase(2), 2400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [wasAnonymous]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md animate-fade-in"
      data-testid="reveal-ceremony"
    >
      {/* Close */}
      <button
        onClick={onClose}
        data-testid="reveal-close-btn"
        className="absolute top-6 right-6 w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-all"
      >
        <X size={16} />
      </button>

      {/* Ambient aurora glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full bg-[#FF3B6B]/25 blur-[130px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full bg-[#7C3AED]/25 blur-[130px] animate-pulse" style={{ animationDelay: "0.7s" }} />
      </div>

      <div className="relative text-center px-6">
        {/* Eyebrow */}
        <p
          className="text-[#FF3B6B] text-[11px] uppercase tracking-[0.35em] mb-3"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
          data-testid="reveal-eyebrow"
        >
          {wasAnonymous ? "Identity Revealed" : "It's a Match"}
        </p>

        {/* Title */}
        <h2
          style={{ fontFamily: "'Bodoni Moda', serif" }}
          className="text-5xl sm:text-6xl text-white tracking-tighter mb-3"
          data-testid="reveal-title"
        >
          It&apos;s a Match!
        </h2>

        <p className="text-white/70 text-base mb-10">
          You and{" "}
          <span
            className={`text-white font-medium transition-all duration-500 ${
              phase < 2 ? "blur-sm" : "blur-0"
            }`}
            data-testid="reveal-name"
          >
            {phase < 2 ? matchPopup.profile.name : revealedName}
          </span>{" "}
          liked each other
        </p>

        {/* Reveal stage */}
        <div className="relative mx-auto w-56 h-56">
          {/* Orbit rings */}
          <div
            className={`absolute inset-0 rounded-full border border-[#FF3B6B]/30 transition-all duration-700 ${
              phase >= 1 ? "scale-110 opacity-100" : "scale-90 opacity-0"
            }`}
          />
          <div
            className={`absolute -inset-4 rounded-full border border-[#7C3AED]/25 transition-all duration-1000 ${
              phase >= 1 ? "scale-110 opacity-100" : "scale-90 opacity-0"
            }`}
            style={{ transitionDelay: "150ms" }}
          />
          <div
            className={`absolute -inset-8 rounded-full border border-white/10 transition-all duration-1000 ${
              phase >= 1 ? "scale-110 opacity-100" : "scale-90 opacity-0"
            }`}
            style={{ transitionDelay: "300ms" }}
          />

          {/* Sparkles */}
          {phase >= 1 && (
            <>
              {[0, 60, 120, 180, 240, 300].map((deg, i) => (
                <div
                  key={deg}
                  className="absolute top-1/2 left-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2"
                  style={{
                    transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(-110px)`,
                    animation: `revealSparkle 1.6s ease-out ${i * 0.08}s both`,
                  }}
                >
                  <Sparkles size={14} className="text-[#FFD57A]" fill="#FFD57A" />
                </div>
              ))}
            </>
          )}

          {/* Silhouette (phase 0-1) */}
          <div
            className={`absolute inset-0 rounded-full overflow-hidden transition-all duration-700 ${
              phase < 2 ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
          >
            <div className="w-full h-full rounded-full bg-gradient-to-br from-[#1A0F2E] via-[#2A0F1D] to-[#0A0A0C] border border-white/10 flex items-center justify-center">
              <UserX size={72} className={`text-white/40 ${phase === 1 ? "blur-md" : ""}`} strokeWidth={1.2} />
            </div>
          </div>

          {/* Revealed photo (phase 2) */}
          <div
            className={`absolute inset-0 rounded-full overflow-hidden ring-4 ring-[#FF3B6B]/60 shadow-[0_0_60px_rgba(255,59,107,0.4)] transition-all duration-1000 ${
              phase >= 2 ? "opacity-100 scale-100" : "opacity-0 scale-125"
            }`}
            data-testid="reveal-photo"
          >
            {revealedPhoto ? (
              <img
                src={revealedPhoto}
                alt={revealedName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#FF3B6B] to-[#7C3AED] flex items-center justify-center">
                <Heart size={64} className="text-white" fill="white" />
              </div>
            )}
          </div>
        </div>

        {/* Bottom hint */}
        <p
          className={`mt-10 text-white/50 text-xs uppercase tracking-[0.25em] transition-opacity duration-700 ${
            phase >= 2 ? "opacity-100" : "opacity-0"
          }`}
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
          data-testid="reveal-hint"
        >
          Say hello from your Matches tab
        </p>
      </div>

      <style>{`
        @keyframes revealSparkle {
          0% { opacity: 0; transform: translate(-50%, -50%) rotate(var(--deg)) translateY(-30px) scale(0); }
          40% { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, -50%) rotate(var(--deg)) translateY(-140px) scale(1.3); }
        }
      `}</style>
    </div>
  );
}
